<template>
	<div class="customize-plan-addon flex  items-center gap-x-4 gap-y-4 py-4" :class="{'customize-plan-addon--no-slider justify-between': !slider, 'flex-wrap': slider}">
		<div class="customize-plan-addon__title flex items-center gap-3" :class="slider && 'customize-plan-addon__title--slider w-full b4:w-auto'">
			<Inline :svg="icon" class="shrink-0" />
			<div class="font-display text-4">
				<span class="flex items-center gap-2">
					{{ title }}
					<CToolTip
						v-if="info"
						class="customize-plan-addon__info shrink-0"
						placement="top"
						:visible-arrow="false"
						show-on-click
					>
						<template #button>
							<Inline svg="info" class="text-icon-subtle" />
						</template>
						<p>{{ info }}</p>
					</CToolTip>
					<button
						v-else-if="infoDialog"
						type="button"
						class="customize-plan-addon__info button--no-style shrink-0"
						@click="$emit('info-click')"
					>
						<Inline svg="info" class="text-icon-subtle" />
					</button>
				</span>
				<span v-if="description" class="block font-body font-normal text-2">{{ description }}</span>
			</div>
			<CIconButton
				v-if="infoLink"
				icon="info"
				:href="infoLink"
			/>
		</div>
		<Slider
			v-if="slider"
			v-model="quantity"
			:show-input="false"
			:min="min"
			:max="max"
			:marks="marks"
			mark-clickable
			:step="step"
			class="grow w-full b7:w-auto order-1 b7:order-none"
		/>
		<div v-else class="b4:text-right border p-3 flex-shrink-0 flex gap-1">
			<label :for="inputId" class="text-4">$</label><input
				:id="inputId"
				v-model="draft"
				class="customize-plan-addon__quantity text-4"
				type="number"
				:min="min"
				:max="max"
				:step="step"
				:style="{ width: `${Math.max(draft.length, 5)}ch` }"
				@change="commitQuantity"
			>
		</div>
		<div v-if="slider" class="customize-plan-addon__slider-container b4:text-right b4:ml-auto b7:ml-0">
			<input
				:id="inputId"
				v-model="draft"
				class="customize-plan-addon__quantity text-4 rounded-none"
				type="number"
				:min="min"
				:max="max"
				:style="{ width: `${Math.max(draft.length + 1, 2)}ch` }"
				@change="commitQuantity"
			>
			<label :for="inputId" class="text-4">&nbsp;{{ quantityLabel }}</label><br>
			{{ formattedTotal }}/mo
		</div>
		<CCallout v-if="showWarning" type="info" size="s" class="w-full" :default-margin-below="false">{{ warningMessage }}</CCallout>
	</div>
</template>

<script lang="ts">
	import { defineComponent, ref, computed, watch, type PropType } from 'vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import Slider from 'src/components/form-controls/Slider.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import { formatUSD } from 'src/utilities/currency';

	// A bucket divider is an empty slider mark drawn as a background-colored notch spanning the
	// runway height, splitting the track into segments at each point where the price tier
	// changes. The recommended marker keeps its own '▼' text style, so dividers are
	// distinguished purely by this inline style.
	const BUCKET_DIVIDER_STYLE: Record<string, string> = {
		top: '0',
		// Sit above the runway bar but below the handle, which shares the slider's z-index but
		// paints after the marks in the DOM, so it would otherwise cover the circle.
		zIndex: '1',
		width: '1px',
		height: '100%',
		padding: '0',
		background: 'var(--bg-primary)',
		transform: 'translateX(-50%)'
	};

	export default defineComponent({
		components: {
			CIconButton,
			Slider,
			CCallout,
			CToolTip
		},
		props: {
			value: { type: Number, required: false, default: 0 },
			title: { type: String, required: true },
			description: { type: String, required: false, default: '' },
			label: { type: String, required: true },
			singularLabel: { type: String, required: false, default: '' },
			icon: { type: String, required: true },
			min: { type: Number, required: true },
			max: { type: Number, required: true },
			recommendedValue: { type: Number, required: false, default: 0 },
			buckets: { type: Array as PropType<number[]>, required: false, default: () => [] },
			info: { type: String, required: false, default: '' },
			infoDialog: { type: Boolean, required: false, default: false },
			showRecommended: { type: Boolean, required: false, default: true },
			infoLink: { type: String, required: false, default: '' },
			slider: { type: Boolean, required: false, default: true },
			step: { type: Number, required: false, default: 1 },
			decimals: { type: Number, required: false, default: 0 },
			pricing: { type: [Number, Function], required: false, default: 0 },
			pricingPer: { type: Number, required: false, default: 1 },
			included: { type: Number, required: false, default: 0 },
			warningThreshold: { type: Number, required: false, default: 0 },
			warningMessage: { type: String, required: false, default: '' }
		},
		emits: ['input', 'info-click'],
		setup(props, { emit }) {
			// Create reactive value, initialized from props
			const quantity = ref(props.value);

			// Watch for changes in the internal value and emit to parent
			watch(quantity, (newValue) => {
				// Ensure we always emit a number
				const numValue = typeof newValue === 'string' ? parseFloat(newValue) || 0 : newValue;
				emit('input', numValue);
			});

			// Watch for changes from parent and update internal value
			watch(() => props.value, (newValue) => {
				if (newValue !== quantity.value) {
					quantity.value = newValue;
				}
			});

			const total = computed(() => {
				if (typeof props.pricing === 'function') {
					return props.pricing(Math.max(quantity.value - props.included, 0));
				}
				return Math.ceil(Math.max(quantity.value - props.included, 0) / props.pricingPer) * props.pricing;
			});
			const formattedTotal = computed(() => formatUSD(total.value).replace(/\.00$/, ''));
			const marks = computed(() => {
				const result: Record<number, string | { label: string; style: Record<string, string> }> = {};
				for (const limit of props.buckets)
					result[limit] = { label: '', style: BUCKET_DIVIDER_STYLE };
				// Set last so a divider coinciding with the suggested value yields the '▼' marker.
				if (props.showRecommended)
					result[props.recommendedValue] = '▼';
				return result;
			});
			const showWarning = computed(() => props.warningThreshold > 0 && quantity.value < props.warningThreshold);
			const quantityLabel = computed(() => quantity.value === 1 && props.singularLabel ? props.singularLabel : props.label);

			const inputId = computed(() => 'customize-plan-addon-' + props.label.toLowerCase().replace(/\W+/g, '-'));

			// The input edits a local draft string rather than quantity itself, so mid-edit
			// text (the "1" on the way to "10") is never validated or rewritten. The draft
			// only commits to quantity on change (blur/Enter), clamped into range.
			const draft = ref(String(props.value));

			watch(quantity, value => {
				draft.value = String(value);
			});

			const commitQuantity = () => {
				let value = Math.min(Math.max(parseFloat(draft.value) || 0, props.min), props.max);
				if (props.decimals !== null)
					value = parseFloat(value.toFixed(props.decimals));
				quantity.value = value;
				// Clamping may leave quantity unchanged, so normalize the draft directly.
				draft.value = String(quantity.value);
			};

			return {
				commitQuantity,
				inputId,
				draft,
				quantity,
				total,
				formattedTotal,
				marks,
				showWarning,
				quantityLabel
			};
		}
	});
</script>

<style lang="scss">
.customize-plan-addon {
	@include b-down($b10) {
		gap: $space__3 !important;
	}

	&__title {

		> i {
			width: 48px;
			height: 48px;
		}

		:root &--slider {
			@include b-up($b7) {
				width: 180px;
			}
		}
	}

	&__slider-container {
		width: 155px;
	}

	// Styled to read as part of the "2 users" text; the snug width comes from the
	// inline ch-based style, which beats the 100px width on inputs below.
	&__quantity {
		max-width: 7ch;
		background: transparent;
		border: none;
		padding: 0;
		vertical-align: baseline;
		color: inherit;
		font-family: inherit;
		line-height: inherit;
		appearance: textfield;

		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			margin: 0;
			appearance: none;
		}

		:not(.border) > & {
			border-bottom: 1px dotted var(--shade-7);
		}
	}

	.el-slider__marks {

		&-text {
			position: absolute;
			top: -600%;
			z-index: $z1;
			padding: var(--spacing-touch-target);
			cursor: pointer;
			transform: translateX(-50%);
			color: var(--icon-subtle);
			user-select: none;

			&:hover {
				color: var(--icon-subtle-hovered);
			}
		}
	}

	input {
		text-align: right;
	}

	.el-slider {

		&__button:focus {
			outline: 1px solid var(--bg-range-selected) !important;
		}

		&__marks-stop {
			display: none;
		}
	}

	@include b-range($b7, 870px) {

		&__title {
			align-items: flex-start !important;
			flex-direction: column;
			gap: var(--spacing-1) !important;
		}

		&:not(.customize-plan-addon--no-slider) &__title {
			width: 110px !important;
		}
	}
}
</style>
